import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, Play, History, Edit2, Trash2, X, ChevronDown, ChevronUp,
  Bot, BarChart3, Loader2, CheckCircle, AlertCircle, Zap, Link,
  Eye, RotateCcw, Database, Clock, Square, Pause, PlayCircle, Activity,
  GripVertical, Layers,
} from 'lucide-react';
import AgentStreamModal from '../components/AgentStreamModal';
import { TradingWebSocket } from '../services/websocketService';
import { WsEvent, WsAgentStatusChange } from '../types/trading';
import {
  TradingAgent, AgentCreate, AgentUpdate, AgentExecution, ModelChainEntry,
} from '../types/agent';
import { Strategy } from '../types/strategy';
import { AgentConnection, DataConnection, AgentConnectionCreate, Direction } from '../types/connection';
import * as agentSvc from '../services/agentService';
import * as strategySvc from '../services/strategyService';
import * as connSvc from '../services/connectionService';
import * as llmSvc from '../services/llmProviderService';
import { formatDate } from '../utils';
import ConfirmationModal from '../components/ConfirmationModal';
import AgentErrorBanner from '../components/AgentErrorBanner';

// Direction badge colors (matching ConnectionsPage)
const DIR_COLOR: Record<Direction, string> = {
  in:   'text-cyan-300 bg-cyan-400/10',
  out:  'text-orange-300 bg-orange-400/10',
  both: 'text-violet-300 bg-violet-400/10',
};

// ---------------------------------------------------------------------------
// Preset agent templates
// ---------------------------------------------------------------------------
const PRESETS: { label: string; type: 'signal' | 'analyst'; prompt: string }[] = [
  {
    label: 'RSI Signal Bot',
    type: 'signal',
    prompt:
      'You are an RSI-focused signal agent. Analyze the RSI value in the market data. ' +
      'Generate a BUY signal when RSI is below 30 (oversold) with high confidence, ' +
      'a SELL signal when RSI is above 70 (overbought), and HOLD otherwise. ' +
      'Always cite the exact RSI value in your reasoning.',
  },
  {
    label: 'MACD Crossover Bot',
    type: 'signal',
    prompt:
      'You are a MACD crossover signal agent. Analyze MACD line and signal line values. ' +
      'Generate a BUY signal on bullish crossover (MACD crosses above signal), ' +
      'a SELL on bearish crossover, and HOLD when no crossover is present. ' +
      'Cite exact MACD and signal line values in your reasoning.',
  },
  {
    label: 'Breakout Detector',
    type: 'signal',
    prompt:
      'You are a price breakout detection agent. Analyze price action relative to ' +
      'Bollinger Bands and recent highs/lows. Generate a BUY signal on confirmed upper ' +
      'band breakout with volume confirmation, SELL on lower band breakout, HOLD otherwise. ' +
      'Cite the band values and current price in your reasoning.',
  },
  {
    label: 'Risk Analyst',
    type: 'analyst',
    prompt:
      'You are a portfolio risk analyst. Review the trade history and performance metrics ' +
      'provided. Identify risk concentrations, drawdown periods, position sizing issues, ' +
      'and win/loss ratio trends. Every finding must cite specific numbers from the data. ' +
      'Provide a risk_score between 0-100 based on observed metrics.',
  },
  {
    label: 'Portfolio Reviewer',
    type: 'analyst',
    prompt:
      'You are a portfolio performance reviewer. Analyze the provided trade history for ' +
      'patterns in profitable vs unprofitable trades. Identify which symbols, timeframes, ' +
      'or conditions produce the best outcomes. Cite specific trade IDs and P&L figures ' +
      'as evidence for every finding.',
  },
  {
    label: 'Performance Auditor',
    type: 'analyst',
    prompt:
      'You are a quantitative performance auditor. Calculate and assess Sharpe ratio trends, ' +
      'max drawdown periods, and daily return consistency from the provided data. ' +
      'Flag any days or trades that deviate significantly from the mean. ' +
      'All findings must reference specific data points from the input.',
  },
];

const OLLAMA_MODELS_DEFAULT = ['mistral:7b', 'deepseek-r1:8b', 'llama3.1:8b', 'llama3.2:3b', 'gemma2:2b'];
const BEDROCK_MODELS_DEFAULT = [
  { id: 'us.anthropic.claude-sonnet-4-5-20251001-v1:0', name: 'Claude Sonnet 4.5' },
  { id: 'anthropic.claude-3-5-sonnet-20241022-v2:0',    name: 'Claude 3.5 Sonnet v2' },
  { id: 'anthropic.claude-3-5-haiku-20241022-v1:0',     name: 'Claude 3.5 Haiku' },
  { id: 'anthropic.claude-3-opus-20240229-v1:0',        name: 'Claude 3 Opus' },
  { id: 'amazon.nova-pro-v1:0',                         name: 'Amazon Nova Pro' },
  { id: 'amazon.nova-lite-v1:0',                        name: 'Amazon Nova Lite' },
  { id: 'meta.llama3-70b-instruct-v1:0',                name: 'Llama 3 70B' },
  { id: 'mistral.mistral-large-2402-v1:0',              name: 'Mistral Large' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Small icon+label button used in the robot control panel I/O row */
const ControlBtn: React.FC<{
  onClick: () => void;
  title: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  activeClass?: string;
  hoverClass?: string;
}> = ({ onClick, title, icon, label, active, activeClass, hoverClass }) => (
  <button
    onClick={onClick}
    title={title}
    className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded border border-transparent text-slate-500 transition-all
      ${hoverClass ?? ''}
      ${active ? (activeClass ?? '') : ''}`}
  >
    {icon}
    <span className="text-[8px] font-mono font-bold tracking-wider leading-none">{label}</span>
  </button>
);

interface AgentCardProps {
  agent: TradingAgent;
  strategy: Strategy | undefined;
  connectionCount: number;
  onExecute: (agent: TradingAgent) => void;
  onHistory: (agent: TradingAgent) => void;
  onEdit: (agent: TradingAgent) => void;
  onDelete: (agent: TradingAgent) => void;
  onConnections: (agent: TradingAgent) => void;
  onDataPreview: (agent: TradingAgent) => void;
  onStream: (agent: TradingAgent) => void;
  executing: boolean;
  isWindowActive: boolean;
  isWindowPaused: boolean;
  isContinuous: boolean;
  onStopWindow: (agent: TradingAgent) => void;
  onStart: (agent: TradingAgent) => void;
  onPause: (agent: TradingAgent) => void;
  onResume: (agent: TradingAgent) => void;
  onStop: (agent: TradingAgent) => void;
  continuousLoading: boolean;
}

const AgentCard: React.FC<AgentCardProps> = ({
  agent, strategy, connectionCount,
  onExecute, onHistory, onEdit, onDelete, onConnections, onDataPreview, onStream,
  executing, isWindowActive, isWindowPaused, isContinuous, onStopWindow,
  onStart, onPause, onResume, onStop, continuousLoading,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isSignal = agent.agent_type === 'signal';
  const isRunning = agent.status === 'running';
  const isPaused = agent.status === 'paused';

  return (
    <div className="bg-slate-800/60 backdrop-blur-md rounded-xl border border-slate-700 p-5 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${isSignal ? 'bg-green-400/10' : 'bg-blue-400/10'}`}>
            {isSignal ? <Zap className="w-4 h-4 text-green-400" /> : <BarChart3 className="w-4 h-4 text-blue-400" />}
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{agent.name}</h3>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded text-xs font-bold tracking-wide ${isSignal ? 'bg-green-400/20 text-green-300' : 'bg-blue-400/20 text-blue-300'}`}>
                {agent.agent_type.toUpperCase()}
              </span>
              {/* Status badge */}
              <span className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${
                isRunning ? 'bg-green-400/20 text-green-300' :
                isPaused ? 'bg-yellow-400/20 text-yellow-300' :
                'bg-slate-400/20 text-slate-400'
              }`}>
                {isRunning && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />}
                {isPaused && <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />}
                {agent.status}
              </span>
              {/* Provider badge */}
              <span className={`px-1.5 py-0.5 rounded text-xs border ${
                agent.llm_provider === 'bedrock'
                  ? 'bg-orange-400/10 text-orange-300 border-orange-400/20'
                  : 'bg-slate-400/10 text-slate-400 border-slate-600/40'
              }`} title={`LLM: ${agent.llm_provider}`}>
                {agent.llm_provider === 'bedrock' ? '☁ Bedrock' : '⚙ Ollama'}
              </span>
              {agent.system_prompt && (
                <span className="px-1.5 py-0.5 rounded text-xs bg-violet-400/10 text-violet-300 border border-violet-400/20" title="Custom system prompt active">
                  advanced
                </span>
              )}
              {agent.schedule_enabled && !isWindowActive && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-amber-400/10 text-amber-300 border border-amber-400/20" title="Schedule active">
                  <Clock className="w-2.5 h-2.5" />
                  scheduled
                </span>
              )}
              {isWindowActive && isContinuous && (
                <span className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs border ${
                  isWindowPaused
                    ? 'bg-yellow-400/20 text-yellow-300 border-yellow-400/40'
                    : 'bg-green-400/20 text-green-300 border-green-400/40 animate-pulse'
                }`} title="Continuous session in progress">
                  <span className={`w-1.5 h-1.5 rounded-full ${isWindowPaused ? 'bg-yellow-400' : 'bg-green-400'}`} />
                  {isWindowPaused ? 'PAUSED' : 'RUNNING'}
                </span>
              )}
              {isWindowActive && !isContinuous && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-green-400/20 text-green-300 border border-green-400/40 animate-pulse" title="Scheduled window in progress">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  ACTIVE
                </span>
              )}
              {connectionCount > 0 && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-cyan-400/10 text-cyan-300" title={`${connectionCount} data connection(s)`}>
                  <Link className="w-2.5 h-2.5" />
                  {connectionCount}
                </span>
              )}
              {agent.trading_enabled && (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${
                  agent.trading_mode === 'live'
                    ? 'bg-orange-400/10 text-orange-300 border-orange-400/20'
                    : 'bg-green-400/10 text-green-300 border-green-400/20'
                }`} title={`Auto-trade: ${agent.trading_mode} on ${agent.exchange_id}`}>
                  {agent.trading_mode === 'live' ? '⚡ live' : '📄 paper'}
                </span>
              )}
              {agent.tools_enabled && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium border bg-violet-400/10 text-violet-300 border-violet-400/20" title="Tool calling enabled">
                  🔧 tools
                </span>
              )}
            </div>
          </div>
        </div>
        <span className="text-xs text-slate-500 bg-slate-700/50 px-2 py-1 rounded font-mono max-w-[140px] truncate" title={agent.model}>{agent.model}</span>
      </div>

      {/* Prompt preview */}
      {agent.prompt ? (
        <p className="text-slate-400 text-xs leading-relaxed line-clamp-2">{agent.prompt}</p>
      ) : (
        <p className="text-slate-600 text-xs italic">No prompt defined — edit to add one</p>
      )}

      {/* Strategy pairing */}
      {strategy && (
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Bot className="w-3 h-3 text-slate-500" />
          <span>Strategy: <span className="text-slate-300">{strategy.name}</span></span>
          <span className="text-slate-600">({strategy.symbol})</span>
        </div>
      )}

      {/* Expanded prompt */}
      {expanded && agent.prompt && (
        <div className="bg-slate-900/60 rounded-lg p-3 text-xs text-slate-300 font-mono leading-relaxed whitespace-pre-wrap border border-slate-700">
          {agent.prompt}
        </div>
      )}
      {agent.prompt && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1 self-start transition">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Hide prompt' : 'Show full prompt'}
        </button>
      )}

      {/* ── Robot Control Panel ─────────────────────────────────────── */}
      <div className="mt-1 rounded-lg border border-slate-600/60 bg-slate-900/70 overflow-hidden">

        {/* Panel header bar */}
        <div className="flex items-center justify-between px-3 py-1 bg-slate-800/80 border-b border-slate-600/40">
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${isRunning ? 'bg-green-400 animate-pulse' : isPaused ? 'bg-yellow-400' : 'bg-slate-600'}`} />
            <span className={`w-1.5 h-1.5 rounded-full ${isRunning ? 'bg-green-400 animate-pulse' : 'bg-slate-600'}`} />
            <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />
          </div>
          <span className="text-[9px] font-mono font-bold tracking-[0.2em] text-slate-500 uppercase">ctrl panel</span>
          <span className="text-[9px] font-mono text-slate-600">{agent.updated_at ? formatDate(agent.updated_at) : formatDate(agent.created_at)}</span>
        </div>

        {/* Row 1 — Session controls (signal agents) */}
        {isSignal && (
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50">
            <span className="text-[9px] font-mono font-bold tracking-widest text-slate-500 uppercase w-10 shrink-0">SYS</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Start */}
              {!isRunning && !isPaused && !isWindowActive && (
                <button
                  onClick={() => onStart(agent)}
                  disabled={continuousLoading || (!agent.prompt && !agent.system_prompt)}
                  title={(!agent.prompt && !agent.system_prompt) ? 'Add a prompt first' : 'Start continuous trading session'}
                  className="flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-500/20 hover:bg-emerald-500/35 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono font-bold tracking-wide disabled:opacity-40 disabled:cursor-not-allowed transition hover:shadow-[0_0_8px_rgba(52,211,153,0.2)]"
                >
                  {continuousLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                  INIT
                </button>
              )}
              {/* Pause */}
              {isRunning && (
                <button
                  onClick={() => onPause(agent)}
                  disabled={continuousLoading}
                  title="Pause session"
                  className="flex items-center gap-1 px-2.5 py-1 rounded bg-yellow-500/20 hover:bg-yellow-500/35 border border-yellow-500/30 text-yellow-300 text-[10px] font-mono font-bold tracking-wide disabled:opacity-40 transition hover:shadow-[0_0_8px_rgba(234,179,8,0.2)]"
                >
                  {continuousLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pause className="w-3 h-3" />}
                  HOLD
                </button>
              )}
              {/* Resume */}
              {isPaused && (
                <button
                  onClick={() => onResume(agent)}
                  disabled={continuousLoading}
                  title="Resume paused session"
                  className="flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-500/20 hover:bg-emerald-500/35 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono font-bold tracking-wide disabled:opacity-40 transition hover:shadow-[0_0_8px_rgba(52,211,153,0.2)]"
                >
                  {continuousLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                  CONT
                </button>
              )}
              {/* Stop (running or paused) */}
              {(isRunning || isPaused) && (
                <button
                  onClick={() => onStop(agent)}
                  disabled={continuousLoading}
                  title="Stop session"
                  className="flex items-center gap-1 px-2.5 py-1 rounded bg-red-500/20 hover:bg-red-500/35 border border-red-500/30 text-red-300 text-[10px] font-mono font-bold tracking-wide disabled:opacity-40 transition hover:shadow-[0_0_8px_rgba(239,68,68,0.2)]"
                >
                  {continuousLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
                  HALT
                </button>
              )}
              {/* Scheduled window stop */}
              {isWindowActive && !isRunning && !isPaused && (
                <button
                  onClick={() => onStopWindow(agent)}
                  title="Stop active scheduled window"
                  className="flex items-center gap-1 px-2.5 py-1 rounded bg-red-500/20 hover:bg-red-500/35 border border-red-500/30 text-red-300 text-[10px] font-mono font-bold tracking-wide transition hover:shadow-[0_0_8px_rgba(239,68,68,0.2)]"
                >
                  <Square className="w-3 h-3" />
                  HALT
                </button>
              )}
              {/* One-shot execute */}
              <button
                onClick={() => onExecute(agent)}
                disabled={executing || (!agent.prompt && !agent.system_prompt)}
                title={(!agent.prompt && !agent.system_prompt) ? 'Add a prompt to execute' : 'One-shot execution'}
                className="flex items-center gap-1 px-2.5 py-1 rounded bg-green-500/20 hover:bg-green-500/35 border border-green-500/30 text-green-300 text-[10px] font-mono font-bold tracking-wide disabled:opacity-40 disabled:cursor-not-allowed transition hover:shadow-[0_0_8px_rgba(34,197,94,0.2)]"
              >
                {executing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                RUN
              </button>
            </div>
          </div>
        )}

        {/* One-shot for analyst agents (no session controls) */}
        {!isSignal && (
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50">
            <span className="text-[9px] font-mono font-bold tracking-widest text-slate-500 uppercase w-10 shrink-0">SYS</span>
            <button
              onClick={() => onExecute(agent)}
              disabled={executing || (!agent.prompt && !agent.system_prompt)}
              title={(!agent.prompt && !agent.system_prompt) ? 'Add a prompt to execute' : 'One-shot execution'}
              className="flex items-center gap-1 px-2.5 py-1 rounded bg-green-500/20 hover:bg-green-500/35 border border-green-500/30 text-green-300 text-[10px] font-mono font-bold tracking-wide disabled:opacity-40 disabled:cursor-not-allowed transition hover:shadow-[0_0_8px_rgba(34,197,94,0.2)]"
            >
              {executing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              RUN
            </button>
          </div>
        )}

        {/* Row 2 — Tool belt */}
        <div className="flex items-center gap-0.5 px-3 py-2">
          <span className="text-[9px] font-mono font-bold tracking-widest text-slate-500 uppercase w-10 shrink-0">I/O</span>
          <div className="flex items-center gap-0.5">
            {isSignal && (
              <ControlBtn
                onClick={() => onStream(agent)}
                title="Live agent stream"
                icon={<Activity className="w-3.5 h-3.5" />}
                label="STRM"
                active={isRunning}
                activeClass="text-emerald-400"
                hoverClass="hover:text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/30"
              />
            )}
            <ControlBtn
              onClick={() => onConnections(agent)}
              title="Manage data connections"
              icon={<Link className="w-3.5 h-3.5" />}
              label="CONN"
              hoverClass="hover:text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/30"
            />
            <ControlBtn
              onClick={() => onDataPreview(agent)}
              title="Preview connection data"
              icon={<Database className="w-3.5 h-3.5" />}
              label="DATA"
              hoverClass="hover:text-sky-400 hover:bg-sky-500/10 hover:border-sky-500/30"
            />
            <ControlBtn
              onClick={() => onHistory(agent)}
              title="Execution history"
              icon={<History className="w-3.5 h-3.5" />}
              label="LOG"
              hoverClass="hover:text-violet-400 hover:bg-violet-500/10 hover:border-violet-500/30"
            />
            <ControlBtn
              onClick={() => onEdit(agent)}
              title="Edit agent"
              icon={<Edit2 className="w-3.5 h-3.5" />}
              label="EDIT"
              hoverClass="hover:text-white hover:bg-slate-500/20 hover:border-slate-400/30"
            />
            <ControlBtn
              onClick={() => onDelete(agent)}
              title="Delete agent"
              icon={<Trash2 className="w-3.5 h-3.5" />}
              label="DEL"
              hoverClass="hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/30"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tool calling constants (outside component to avoid re-creation on each render)
// ---------------------------------------------------------------------------

const ALL_TOOL_NAMES = ['get_ohlcv', 'get_ticker', 'get_indicators', 'get_orderbook', 'get_multi_timeframe', 'get_news', 'get_market_news', 'search_news'] as const;
type ToolName = typeof ALL_TOOL_NAMES[number];
const TOOL_LABELS: Record<ToolName, { label: string; desc: string }> = {
  get_ohlcv:           { label: 'OHLCV Candles',       desc: 'Fetch candlestick data for any symbol & timeframe' },
  get_ticker:          { label: 'Live Ticker',          desc: 'Real-time price, bid/ask, 24h change & volume' },
  get_indicators:      { label: 'Technical Indicators', desc: 'RSI, MACD, Bollinger Bands, EMA, Stochastic, VWAP, S/R' },
  get_orderbook:       { label: 'Order Book Depth',     desc: 'Bid/ask depth with buy/sell imbalance analysis' },
  get_multi_timeframe: { label: 'Multi-Timeframe',      desc: '15m / 1h / 4h / 1d snapshots with trend alignment' },
  get_news:            { label: 'Symbol News',          desc: 'Recent headlines for specific symbols (earnings, analyst ratings)' },
  get_market_news:     { label: 'Market News',          desc: 'General market & macro news (Fed, geopolitical, sector-wide)' },
  search_news:         { label: 'Search News',          desc: 'Search recent news by keyword (e.g. "oil exports", "tariff")' },
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const AgentsPage: React.FC = () => {
  const [agents, setAgents] = useState<TradingAgent[]>([]);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<TradingAgent | null>(null);
  const [form, setForm] = useState<AgentCreate>({
    name: '', capital: 10000, agent_type: 'signal', prompt: '', model: 'llama3.2:3b',
    llm_provider: 'ollama', strategy_id: null,
  });
  const [promptMode, setPromptMode] = useState<'basic' | 'advanced'>('basic');
  const [systemPromptText, setSystemPromptText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalTab, setModalTab] = useState<'config' | 'schedule'>('config');

  const DEFAULT_SCHEDULE_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
  const [scheduleForm, setScheduleForm] = useState({
    schedule_enabled: false,
    schedule_days: DEFAULT_SCHEDULE_DAYS,
    schedule_timezone: 'America/New_York',
    schedule_start_time: '09:30',
    schedule_end_time: '10:30',
  });
  const [tradingForm, setTradingForm] = useState({
    trading_enabled: false,
    trading_mode: 'paper' as 'paper' | 'live',
    exchange_type: 'crypto' as 'crypto' | 'equities',
    exchange_id: 'kraken',
    cash_limit: '' as string,  // stored as string for input, converted on save
  });
  const [toolsForm, setToolsForm] = useState({
    tools_enabled: false,
    available_tools: [...ALL_TOOL_NAMES] as string[],
  });
  const [modelChain, setModelChain] = useState<ModelChainEntry[]>([]);
  // Active window tracking: { agent_id → { is_running, is_paused, is_continuous } }
  const [activeWindowMap, setActiveWindowMap] = useState<Record<string, { is_running: boolean; is_paused: boolean; is_continuous: boolean }>>({});
  const [continuousLoadingId, setContinuousLoadingId] = useState<string | null>(null);

  // Live stream modal
  const [streamModal, setStreamModal] = useState<{ open: boolean; agent: TradingAgent | null }>({ open: false, agent: null });
  const wsRef = useRef<TradingWebSocket | null>(null);
  // Available models based on active provider
  const [availableModels, setAvailableModels] = useState<{ id: string; name?: string }[]>(
    OLLAMA_MODELS_DEFAULT.map(m => ({ id: m }))
  );

  // Connections side panel
  const [connPanel, setConnPanel] = useState<{ open: boolean; agent: TradingAgent | null }>({ open: false, agent: null });
  const [agentConns, setAgentConns] = useState<AgentConnection[]>([]);
  const [allConns, setAllConns] = useState<DataConnection[]>([]);
  const [connsLoading, setConnsLoading] = useState(false);
  const [attachModal, setAttachModal] = useState(false);
  const [attachForm, setAttachForm] = useState<{ connection_id: string; direction: Direction; config: string }>({
    connection_id: '', direction: 'in', config: '',
  });
  const [attachConfigError, setAttachConfigError] = useState<string | null>(null);
  const [editingConnId, setEditingConnId] = useState<string | null>(null); // AgentConnection.id being edited
  const [attaching, setAttaching] = useState(false);
  const [connCountMap, setConnCountMap] = useState<Record<string, number>>({});

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<TradingAgent | null>(null);

  // Input modal (signal agents need market data before executing)
  const [inputModal, setInputModal] = useState<{ open: boolean; agent: TradingAgent | null }>({ open: false, agent: null });
  const [inputData, setInputData] = useState('');
  const [inputContext, setInputContext] = useState('');

  // Execution modal
  const [execModal, setExecModal] = useState<{
    open: boolean; agentName: string; status: 'running' | 'completed' | 'failed';
    result: AgentExecution | null; elapsed: number;
  }>({ open: false, agentName: '', status: 'running', result: null, elapsed: 0 });
  const execTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [executingId, setExecutingId] = useState<string | null>(null);

  // History modal
  const [historyModal, setHistoryModal] = useState<{ open: boolean; agent: TradingAgent | null; executions: AgentExecution[] }>({
    open: false, agent: null, executions: [],
  });
  const [expandedExec, setExpandedExec] = useState<Set<string>>(new Set());

  // Connection data preview modal
  const [dataPreviewModal, setDataPreviewModal] = useState<{ open: boolean; agent: TradingAgent | null; data: unknown; loading: boolean }>({
    open: false, agent: null, data: null, loading: false,
  });

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadData = async () => {
    try {
      const [agentsData, strategiesData] = await Promise.all([
        agentSvc.getAgents(),
        strategySvc.getStrategies(),
      ]);
      setAgents(agentsData);
      setStrategies(strategiesData);
      // Load connection counts for all agents (best effort)
      const counts: Record<string, number> = {};
      await Promise.all(agentsData.map(async (a) => {
        try {
          const ac = await connSvc.getAgentConnections(a.id);
          counts[a.id] = ac.length;
        } catch { /* ignore */ }
      }));
      setConnCountMap(counts);
    } catch (err) {
      console.error('Failed to load agents/strategies', err);
    } finally {
      setLoading(false);
    }
  };

  const loadActiveWindows = async () => {
    try {
      const windows = await agentSvc.getActiveWindows();
      const map: Record<string, { is_running: boolean; is_paused: boolean; is_continuous: boolean }> = {};
      for (const w of windows) {
        map[w.agent_id] = {
          is_running: w.is_running,
          is_paused: w.is_paused,
          is_continuous: w.is_continuous,
        };
      }
      setActiveWindowMap(map);
    } catch { /* best effort */ }
  };

  const loadModelsForProvider = async (prov: 'ollama' | 'bedrock') => {
    try {
      const res = await llmSvc.getProviderModels(prov);
      setAvailableModels(res.models.map(m => ({ id: m.id, name: m.name })));
    } catch {
      if (prov === 'bedrock') {
        setAvailableModels(BEDROCK_MODELS_DEFAULT);
      } else {
        setAvailableModels(OLLAMA_MODELS_DEFAULT.map(m => ({ id: m })));
      }
    }
  };

  useEffect(() => {
    loadData();
    loadActiveWindows();
    // Poll active windows every 10 seconds
    const interval = setInterval(() => {
      loadActiveWindows();
      // Re-fetch agents to get updated status
      agentSvc.getAgents().then(setAgents).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket — real-time agent status + cycle stream
  useEffect(() => {
    const ws = new TradingWebSocket((event: WsEvent) => {
      // Instantly update agent status in the list when status changes
      if (event.type === 'agent_status_change') {
        const { agent_id, status } = (event as WsAgentStatusChange).data;
        setAgents(prev =>
          prev.map(a => a.id === agent_id ? { ...a, status } : a)
        );
        // Also update activeWindowMap for immediate badge refresh
        if (status === 'idle') {
          setActiveWindowMap(prev => {
            const next = { ...prev };
            delete next[agent_id];
            return next;
          });
        }
      }
    });
    ws.connect();
    wsRef.current = ws;
    return () => { ws.disconnect(); wsRef.current = null; };
  }, []);

  // ---------------------------------------------------------------------------
  // Create / Edit
  // ---------------------------------------------------------------------------

  const openCreate = () => {
    setEditingAgent(null);
    setForm({ name: '', capital: 10000, agent_type: 'signal', prompt: '', model: 'llama3.2:3b', llm_provider: 'ollama', strategy_id: null });
    loadModelsForProvider('ollama');
    setPromptMode('basic');
    setSystemPromptText('');
    setModalTab('config');
    setScheduleForm({
      schedule_enabled: false,
      schedule_days: DEFAULT_SCHEDULE_DAYS,
      schedule_timezone: 'America/New_York',
      schedule_start_time: '09:30',
      schedule_end_time: '10:30',
    });
    setTradingForm({
      trading_enabled: false,
      trading_mode: 'paper',
      exchange_type: 'crypto',
      exchange_id: 'kraken',
      cash_limit: '',
    });
    setToolsForm({
      tools_enabled: false,
      available_tools: [...ALL_TOOL_NAMES],
    });
    setModelChain([]);
    setShowModal(true);
  };

  const openEdit = (agent: TradingAgent) => {
    setEditingAgent(agent);
    const prov = agent.llm_provider || 'ollama';
    setForm({
      name: agent.name,
      capital: agent.capital,
      agent_type: agent.agent_type,
      prompt: agent.prompt ?? '',
      model: agent.model,
      llm_provider: prov,
      strategy_id: agent.strategy_id,
    });
    loadModelsForProvider(prov);
    const hasOverride = !!agent.system_prompt;
    setPromptMode(hasOverride ? 'advanced' : 'basic');
    setSystemPromptText(agent.system_prompt ?? '');
    setModalTab('config');
    setScheduleForm({
      schedule_enabled: agent.schedule_enabled ?? false,
      schedule_days: agent.schedule_days ?? DEFAULT_SCHEDULE_DAYS,
      schedule_timezone: agent.schedule_timezone ?? 'America/New_York',
      schedule_start_time: agent.schedule_start_time ?? '09:30',
      schedule_end_time: agent.schedule_end_time ?? '10:30',
    });
    setTradingForm({
      trading_enabled: agent.trading_enabled ?? false,
      trading_mode: (agent.trading_mode as 'paper' | 'live') ?? 'paper',
      exchange_type: (agent.exchange_type as 'crypto' | 'equities') ?? 'crypto',
      exchange_id: agent.exchange_id ?? 'kraken',
      cash_limit: agent.cash_limit != null ? String(agent.cash_limit) : '',
    });
    setToolsForm({
      tools_enabled: agent.tools_enabled ?? false,
      available_tools: agent.available_tools ?? [...ALL_TOOL_NAMES],
    });
    setModelChain(agent.model_chain ?? []);
    setShowModal(true);
  };

  const loadPreviewPrompt = async () => {
    if (!editingAgent) return;
    setPreviewLoading(true);
    try {
      const preview = await agentSvc.previewAgentPrompt(editingAgent.id);
      setSystemPromptText(preview.system_prompt);
    } catch (err) {
      console.error('Failed to load prompt preview', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const switchToAdvanced = async () => {
    setPromptMode('advanced');
    // If no text yet, pre-populate from backend preview (or assemble locally)
    if (!systemPromptText) {
      await loadPreviewPrompt();
    }
  };

  const resetToTemplate = async () => {
    await loadPreviewPrompt();
  };

  const applyPreset = (preset: typeof PRESETS[0]) => {
    setForm(f => ({ ...f, agent_type: preset.type, prompt: preset.prompt }));
  };

  const saveAgent = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const effectiveSystemPrompt = promptMode === 'advanced' && systemPromptText.trim()
      ? systemPromptText.trim()
      : null;
    try {
      const schedulePayload = {
        schedule_enabled: scheduleForm.schedule_enabled,
        schedule_days: scheduleForm.schedule_days,
        schedule_timezone: scheduleForm.schedule_timezone,
        schedule_start_time: scheduleForm.schedule_start_time || null,
        schedule_end_time: form.agent_type === 'signal' ? (scheduleForm.schedule_end_time || null) : null,
      };
      const tradingPayload = {
        trading_enabled: tradingForm.trading_enabled,
        trading_mode: tradingForm.trading_mode,
        exchange_type: tradingForm.exchange_type,
        exchange_id: tradingForm.exchange_id,
        cash_limit: tradingForm.cash_limit ? Number(tradingForm.cash_limit) : null,
      };
      const toolsPayload = {
        tools_enabled: toolsForm.tools_enabled,
        available_tools: toolsForm.tools_enabled
          ? (toolsForm.available_tools.length === ALL_TOOL_NAMES.length ? null : toolsForm.available_tools)
          : null,
      };
      // Derive primary model/provider from first chain entry if chain is set;
      // keeps legacy model field consistent with what the runtime will actually use.
      const effectiveModel = modelChain.length > 0 ? modelChain[0].model : form.model;
      const effectiveProvider = modelChain.length > 0 ? modelChain[0].llm_provider : form.llm_provider;
      const chainPayload = {
        model_chain: modelChain.length > 0 ? modelChain : null,
        model: effectiveModel,
        llm_provider: effectiveProvider,
      };
      if (editingAgent) {
        const updates: AgentUpdate = {
          name: form.name,
          agent_type: form.agent_type,
          prompt: form.prompt,
          system_prompt: effectiveSystemPrompt,
          strategy_id: form.strategy_id,
          ...schedulePayload,
          ...tradingPayload,
          ...toolsPayload,
          ...chainPayload,
        };
        await agentSvc.updateAgent(editingAgent.id, updates);
      } else {
        await agentSvc.createAgent({ ...form, system_prompt: effectiveSystemPrompt, ...schedulePayload, ...tradingPayload, ...toolsPayload, ...chainPayload });
      }
      setShowModal(false);
      await loadData();
    } catch (err) {
      console.error('Save agent failed', err);
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Window management
  // ---------------------------------------------------------------------------

  const handleStopWindow = async (agent: TradingAgent) => {
    try {
      await agentSvc.stopAgentWindow(agent.id);
      await loadActiveWindows();
    } catch (err) {
      console.error('Stop window failed', err);
    }
  };

  const handleStart = async (agent: TradingAgent) => {
    setContinuousLoadingId(agent.id);
    try {
      await agentSvc.startAgent(agent.id);
      await Promise.all([loadData(), loadActiveWindows()]);
    } catch (err) {
      console.error('Start agent failed', err);
    } finally {
      setContinuousLoadingId(null);
    }
  };

  const handlePause = async (agent: TradingAgent) => {
    setContinuousLoadingId(agent.id);
    try {
      await agentSvc.pauseAgent(agent.id);
      await Promise.all([loadData(), loadActiveWindows()]);
    } catch (err) {
      console.error('Pause agent failed', err);
    } finally {
      setContinuousLoadingId(null);
    }
  };

  const handleResume = async (agent: TradingAgent) => {
    setContinuousLoadingId(agent.id);
    try {
      await agentSvc.resumeAgent(agent.id);
      await Promise.all([loadData(), loadActiveWindows()]);
    } catch (err) {
      console.error('Resume agent failed', err);
    } finally {
      setContinuousLoadingId(null);
    }
  };

  const handleStop = async (agent: TradingAgent) => {
    setContinuousLoadingId(agent.id);
    try {
      await agentSvc.stopAgent(agent.id);
      await Promise.all([loadData(), loadActiveWindows()]);
    } catch (err) {
      console.error('Stop agent failed', err);
    } finally {
      setContinuousLoadingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await agentSvc.deleteAgent(deleteTarget.id);
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      console.error('Delete agent failed', err);
    }
  };

  // ---------------------------------------------------------------------------
  // Execute
  // ---------------------------------------------------------------------------

  const openExecute = (agent: TradingAgent) => {
    // Both agent types: show optional data input modal.
    // Backend auto-hydrates from connected data sources when input is empty/'{}'.
    setInputData('');
    setInputContext('');
    setInputModal({ open: true, agent });
  };

  const runAgent = async (agent: TradingAgent, data: string, ctx: Record<string, unknown>) => {
    setInputModal({ open: false, agent: null });
    setExecutingId(agent.id);
    setExecModal({ open: true, agentName: agent.name, status: 'running', result: null, elapsed: 0 });

    let elapsed = 0;
    execTimerRef.current = setInterval(() => {
      elapsed += 100;
      setExecModal(prev => ({ ...prev, elapsed }));
    }, 100);

    try {
      const result = await agentSvc.executeAgent(agent.id, data, ctx);
      clearInterval(execTimerRef.current!);
      setExecModal(prev => ({
        ...prev,
        status: result.success ? 'completed' : 'failed',
        result,
      }));
    } catch (err: any) {
      clearInterval(execTimerRef.current!);
      const errMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        err?.message ||
        'Unknown error';
      setExecModal(prev => ({
        ...prev,
        status: 'failed',
        result: { id: '', agent_id: agent.id, success: false, result: null, duration_ms: null, model: null, parse_attempts: 0, started_at: new Date().toISOString(), completed_at: null, error: errMsg },
      }));
    } finally {
      setExecutingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // History
  // ---------------------------------------------------------------------------

  const openHistory = async (agent: TradingAgent) => {
    try {
      const executions = await agentSvc.getAgentHistory(agent.id, 20);
      setHistoryModal({ open: true, agent, executions });
      setExpandedExec(new Set());
    } catch (err) {
      console.error('Failed to load history', err);
    }
  };

  const openDataPreview = async (agent: TradingAgent) => {
    setDataPreviewModal({ open: true, agent, data: null, loading: true });
    try {
      const preview = await agentSvc.previewConnectionData(agent.id);
      setDataPreviewModal(prev => ({ ...prev, data: preview.data, loading: false }));
    } catch (err) {
      console.error('Failed to load connection data', err);
      setDataPreviewModal(prev => ({ ...prev, data: { error: 'Failed to load connection data' }, loading: false }));
    }
  };

  const toggleExecExpand = (id: string) => {
    setExpandedExec(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Connections panel
  // ---------------------------------------------------------------------------

  const handleStream = useCallback((agent: TradingAgent) => {
    setStreamModal({ open: true, agent });
  }, []);

  const openConnections = async (agent: TradingAgent) => {
    setConnPanel({ open: true, agent });
    setConnsLoading(true);
    try {
      const [ac, allC] = await Promise.all([
        connSvc.getAgentConnections(agent.id),
        connSvc.getConnections(),
      ]);
      setAgentConns(ac);
      setAllConns(allC);
    } catch (err) {
      console.error('Failed to load connections', err);
    } finally {
      setConnsLoading(false);
    }
  };

  const handleAttach = async () => {
    if (!connPanel.agent || !attachForm.connection_id) return;
    setAttachConfigError(null);

    let parsedConfig: Record<string, unknown> | undefined;
    if (attachForm.config.trim()) {
      try {
        parsedConfig = JSON.parse(attachForm.config);
      } catch (e: any) {
        setAttachConfigError(`Invalid JSON: ${e.message}`);
        return;
      }
    }

    setAttaching(true);
    try {
      // Edit mode: detach existing then re-attach with new config
      if (editingConnId) {
        await connSvc.detachConnection(connPanel.agent.id, editingConnId);
      }
      const payload: AgentConnectionCreate = {
        connection_id: attachForm.connection_id,
        direction: attachForm.direction,
        config: parsedConfig,
      };
      await connSvc.attachConnection(connPanel.agent.id, payload);
      const updated = await connSvc.getAgentConnections(connPanel.agent.id);
      setAgentConns(updated);
      setConnCountMap(m => ({ ...m, [connPanel.agent!.id]: updated.length }));
      setAttachModal(false);
      setAttachForm({ connection_id: '', direction: 'in', config: '' });
      setEditingConnId(null);
    } catch (err) {
      console.error('Attach failed', err);
    } finally {
      setAttaching(false);
    }
  };

  const handleDetach = async (agentConnId: string) => {
    if (!connPanel.agent) return;
    try {
      await connSvc.detachConnection(connPanel.agent.id, agentConnId);
      const updated = await connSvc.getAgentConnections(connPanel.agent.id);
      setAgentConns(updated);
      setConnCountMap(m => ({ ...m, [connPanel.agent!.id]: updated.length }));
    } catch (err) {
      console.error('Detach failed', err);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const strategyMap = Object.fromEntries(strategies.map(s => [s.id, s]));

  const renderResult = (exec: AgentExecution) => {
    if (!exec.result) return <span className="text-slate-500 text-xs">No result</span>;
    return (
      <pre className="text-xs text-green-300 font-mono bg-slate-950 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-64">
        {JSON.stringify(exec.result, null, 2)}
      </pre>
    );
  };

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Trading Agents</h1>
          <p className="text-slate-400">Create trading agents powered by Ollama or AWS Bedrock to execute trades and analyse portfolios</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm transition"
        >
          <Plus className="w-4 h-4" />
          New Agent
        </button>
      </div>

      {/* Agent grid */}
      {loading ? (
        <div className="text-center text-slate-400 py-16">Loading agents...</div>
      ) : agents.length === 0 ? (
        <div className="bg-slate-800/50 backdrop-blur-md rounded-xl border border-slate-700 p-12 text-center">
          <Bot className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 mb-2">No agents yet.</p>
          <p className="text-slate-500 text-sm">Click <span className="text-blue-400">New Agent</span> to create your first LangGraph trading agent.</p>
        </div>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
          {agents.map(agent => {
            const windowInfo = activeWindowMap[agent.id];
            return (
              <AgentCard
                key={agent.id}
                agent={agent}
                strategy={agent.strategy_id ? strategyMap[agent.strategy_id] : undefined}
                connectionCount={connCountMap[agent.id] ?? 0}
                onExecute={openExecute}
                onHistory={openHistory}
                onDataPreview={openDataPreview}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
                onConnections={openConnections}
                onStream={handleStream}
                executing={executingId === agent.id}
                isWindowActive={!!windowInfo?.is_running}
                isWindowPaused={!!windowInfo?.is_paused}
                isContinuous={!!windowInfo?.is_continuous || agent.status === 'running' || agent.status === 'paused'}
                onStopWindow={handleStopWindow}
                onStart={handleStart}
                onPause={handlePause}
                onResume={handleResume}
                onStop={handleStop}
                continuousLoading={continuousLoadingId === agent.id}
              />
            );
          })}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Create / Edit Modal                                                  */}
      {/* ------------------------------------------------------------------ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">{editingAgent ? 'Edit Agent' : 'New Agent'}</h2>
              <div className="flex items-center gap-3">
                {/* Modal tabs */}
                <div className="flex items-center gap-1 bg-slate-900 border border-slate-600 rounded-lg p-0.5">
                  <button
                    onClick={() => setModalTab('config')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition flex items-center gap-1.5 ${modalTab === 'config' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  >
                    <Bot className="w-3 h-3" /> Config
                  </button>
                  <button
                    onClick={() => setModalTab('schedule')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition flex items-center gap-1.5 ${modalTab === 'schedule' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  >
                    <Clock className="w-3 h-3" /> Schedule
                    {scheduleForm.schedule_enabled && (
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                    )}
                  </button>
                  <button
                    onClick={() => setModalTab('trading' as any)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition flex items-center gap-1.5 ${(modalTab as string) === 'trading' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  >
                    ⚡ Trading
                    {tradingForm.trading_enabled && (
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                    )}
                  </button>
                  <button
                    onClick={() => setModalTab('tools' as any)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition flex items-center gap-1.5 ${(modalTab as string) === 'tools' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  >
                    🔧 Tools
                    {toolsForm.tools_enabled && (
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block" />
                    )}
                  </button>
                  <button
                    onClick={() => setModalTab('models' as any)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition flex items-center gap-1.5 ${(modalTab as string) === 'models' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  >
                    <Layers className="w-3 h-3" /> Models
                    {modelChain.length > 0 && (
                      <span className="w-4 h-4 rounded-full bg-cyan-500 text-white text-[10px] font-bold flex items-center justify-center">
                        {modelChain.length}
                      </span>
                    )}
                  </button>
                </div>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white transition"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {(modalTab as string) === 'models' ? (
                /* ---------------------------------------------------------------- */
                /* Models Tab — ordered fallback chain                               */
                /* ---------------------------------------------------------------- */
                <div className="space-y-4">
                  <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 text-xs text-cyan-300 space-y-1">
                    <p className="text-white font-medium text-sm mb-2 flex items-center gap-2">
                      <Layers className="w-4 h-4" /> Model Fallback Chain
                    </p>
                    <p>Add up to 10 models in priority order. When a model hits its quota or is unavailable, the agent automatically tries the next one — ensuring uninterrupted operation.</p>
                    <p className="text-cyan-400 pt-1">Position 1 is always tried first. The primary model on the Config tab syncs to position 1 when you save.</p>
                  </div>

                  {/* Chain entries */}
                  <div className="space-y-2">
                    {modelChain.map((entry, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-3 bg-slate-900/60 border border-slate-700 rounded-lg"
                      >
                        <GripVertical className="w-4 h-4 text-slate-600 flex-shrink-0" />
                        <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${idx === 0 ? 'bg-cyan-500 text-white' : 'bg-slate-700 text-slate-300'}`}>
                          {idx + 1}
                        </span>

                        {/* Provider toggle */}
                        <div className="flex rounded-md overflow-hidden border border-slate-600 flex-shrink-0">
                          {(['ollama', 'bedrock'] as const).map(p => (
                            <button
                              key={p}
                              onClick={() => setModelChain(c => c.map((e, i) => i === idx ? { ...e, llm_provider: p } : e))}
                              className={`px-2 py-1 text-xs font-medium transition ${
                                entry.llm_provider === p
                                  ? p === 'bedrock' ? 'bg-orange-500/30 text-orange-300' : 'bg-slate-500/30 text-slate-200'
                                  : 'text-slate-500 hover:text-slate-300'
                              }`}
                            >
                              {p === 'ollama' ? '⚙' : '☁'} {p}
                            </button>
                          ))}
                        </div>

                        {/* Model ID input */}
                        <input
                          value={entry.model}
                          onChange={e => setModelChain(c => c.map((en, i) => i === idx ? { ...en, model: e.target.value } : en))}
                          placeholder={entry.llm_provider === 'bedrock' ? 'e.g. us.anthropic.claude-haiku-4-5-20251001-v1:0' : 'e.g. llama3.2:3b'}
                          className="flex-1 bg-slate-800 border border-slate-600 text-white text-xs font-mono rounded-md px-2 py-1.5 focus:outline-none focus:border-cyan-500 min-w-0"
                        />

                        {/* Move up / down */}
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            disabled={idx === 0}
                            onClick={() => setModelChain(c => { const n = [...c]; [n[idx-1], n[idx]] = [n[idx], n[idx-1]]; return n; })}
                            className="p-1 rounded text-slate-500 hover:text-slate-300 disabled:opacity-20 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button
                            disabled={idx === modelChain.length - 1}
                            onClick={() => setModelChain(c => { const n = [...c]; [n[idx], n[idx+1]] = [n[idx+1], n[idx]]; return n; })}
                            className="p-1 rounded text-slate-500 hover:text-slate-300 disabled:opacity-20 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Remove */}
                        <button
                          onClick={() => setModelChain(c => c.filter((_, i) => i !== idx))}
                          className="p-1 rounded text-slate-500 hover:text-red-400 transition flex-shrink-0"
                          title="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add model button */}
                  {modelChain.length < 10 && (
                    <button
                      onClick={() => {
                        const lastProvider = modelChain.length > 0 ? modelChain[modelChain.length - 1].llm_provider : form.llm_provider ?? 'ollama';
                        setModelChain(c => [...c, { model: '', llm_provider: lastProvider as 'ollama' | 'bedrock' }]);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-slate-600 text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400 text-sm transition"
                    >
                      <Plus className="w-4 h-4" /> Add Model
                    </button>
                  )}

                  {/* Populate from Config tab shortcut */}
                  {modelChain.length === 0 && form.model && (
                    <div className="bg-slate-900/40 border border-slate-700 rounded-lg p-4 text-sm text-slate-400 text-center space-y-2">
                      <p>No chain configured — the agent will use the single model from the Config tab.</p>
                      <button
                        onClick={() => setModelChain([{ model: form.model!, llm_provider: form.llm_provider ?? 'ollama' }])}
                        className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                      >
                        Start chain from current model ({form.model})
                      </button>
                    </div>
                  )}

                  {modelChain.length > 0 && (
                    <div className="bg-slate-900/40 border border-slate-700 rounded-lg p-3 text-xs text-slate-400 space-y-1">
                      <p className="text-white font-medium mb-1">Fallback behaviour</p>
                      <p>Quota / rate-limit / model-unavailable errors trigger automatic fallback to the next model.</p>
                      <p>Credential errors, network failures, and bad JSON responses do <em>not</em> trigger chain fallback — they fail immediately so you can diagnose them.</p>
                    </div>
                  )}
                </div>
              ) : (modalTab as string) === 'tools' ? (
                /* ---------------------------------------------------------------- */
                /* Tools Tab                                                         */
                /* ---------------------------------------------------------------- */
                <div className="space-y-5">
                  {/* Enable toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-900/60 rounded-lg border border-slate-700">
                    <div>
                      <p className="text-sm font-medium text-white">Enable Tool Calling</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Allow the agent to query live market data mid-reasoning before committing to a signal.
                        Only applies to signal agents using Ollama or Bedrock.
                      </p>
                    </div>
                    <button
                      onClick={() => setToolsForm(f => ({ ...f, tools_enabled: !f.tools_enabled }))}
                      className={`relative w-11 h-6 rounded-full transition ${toolsForm.tools_enabled ? 'bg-violet-500' : 'bg-slate-600'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${toolsForm.tools_enabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>

                  {toolsForm.tools_enabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Available Tools</label>
                        <p className="text-xs text-slate-500 mb-3">
                          Select which tools the agent can call. Market tools use public data. News tools use Alpaca/Finnhub credentials from env vars or connections.
                        </p>
                        <div className="flex flex-col gap-2">
                          {(ALL_TOOL_NAMES as readonly ToolName[]).map(name => {
                            const { label, desc } = TOOL_LABELS[name];
                            const isSelected = toolsForm.available_tools.includes(name);
                            return (
                              <button
                                key={name}
                                onClick={() => setToolsForm(f => ({
                                  ...f,
                                  available_tools: isSelected
                                    ? f.available_tools.filter(t => t !== name)
                                    : [...f.available_tools, name],
                                }))}
                                className={`flex items-start gap-3 p-3 rounded-lg border text-left transition ${
                                  isSelected
                                    ? 'bg-violet-500/10 border-violet-500/40 text-white'
                                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                                }`}
                              >
                                <span className={`w-4 h-4 rounded border flex-shrink-0 mt-0.5 flex items-center justify-center transition ${isSelected ? 'bg-violet-500 border-violet-500' : 'border-slate-600'}`}>
                                  {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                                </span>
                                <div>
                                  <div className="text-xs font-medium">{label}</div>
                                  <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
                                  <div className="text-xs text-slate-600 font-mono mt-0.5">{name}()</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 text-xs text-slate-400 space-y-1">
                        <p className="text-white font-medium text-sm mb-2">How Tool Calling Works</p>
                        <p>1. Agent receives pre-fetched market data as context</p>
                        <p>2. LLM can call selected tools to fetch additional live data</p>
                        <p>3. Tool results are fed back into the LLM (up to 4 iterations)</p>
                        <p>4. LLM commits to a final buy/sell/hold signal</p>
                        <p className="text-slate-500 pt-1">The exchange used matches the Trading tab exchange setting.</p>
                      </div>
                    </>
                  )}
                </div>
              ) : (modalTab as string) === 'trading' ? (
                /* ---------------------------------------------------------------- */
                /* Trading Tab                                                       */
                /* ---------------------------------------------------------------- */
                <div className="space-y-5">
                  {/* Enable toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-900/60 rounded-lg border border-slate-700">
                    <div>
                      <p className="text-sm font-medium text-white">Auto-Execute Trades</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        When enabled, BUY/SELL signals automatically trigger trades.
                        Only applies to signal agents.
                      </p>
                    </div>
                    <button
                      onClick={() => setTradingForm(f => ({ ...f, trading_enabled: !f.trading_enabled }))}
                      className={`relative w-11 h-6 rounded-full transition ${tradingForm.trading_enabled ? 'bg-green-500' : 'bg-slate-600'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${tradingForm.trading_enabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>

                  {tradingForm.trading_enabled && (
                    <>
                      {/* Trading Mode */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Trading Mode</label>
                        <div className="flex rounded-lg overflow-hidden border border-slate-600">
                          {[
                            { label: '📄 Paper (Forward Test)', val: 'paper' as const },
                            { label: '⚡ Live Trading',         val: 'live'  as const },
                          ].map(({ label, val }) => (
                            <button
                              key={val}
                              onClick={() => setTradingForm(f => ({ ...f, trading_mode: val }))}
                              className={`flex-1 px-4 py-2 text-xs font-medium transition ${
                                tradingForm.trading_mode === val
                                  ? val === 'paper' ? 'bg-blue-600 text-white' : 'bg-orange-600 text-white'
                                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        {tradingForm.trading_mode === 'live' && (
                          <div className="mt-2 bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2 text-xs text-orange-300">
                            ⚠ Live mode executes real orders with real funds. Ensure exchange credentials are configured in Exchange Settings.
                          </div>
                        )}
                      </div>

                      {/* Asset Class */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Asset Class</label>
                        <div className="flex rounded-lg overflow-hidden border border-slate-600">
                          {[
                            { label: '₿ Crypto',    val: 'crypto'    as const },
                            { label: '📈 Equities', val: 'equities'  as const },
                          ].map(({ label, val }) => (
                            <button
                              key={val}
                              onClick={() => {
                                setTradingForm(f => ({
                                  ...f,
                                  exchange_type: val,
                                  exchange_id: val === 'crypto' ? 'kraken' : 'alpaca',
                                }));
                              }}
                              className={`flex-1 px-4 py-2 text-xs font-medium transition ${
                                tradingForm.exchange_type === val ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Exchange */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Exchange</label>
                        <select
                          value={tradingForm.exchange_id}
                          onChange={e => setTradingForm(f => ({ ...f, exchange_id: e.target.value }))}
                          className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2"
                        >
                          {tradingForm.exchange_type === 'crypto' ? (
                            <>
                              <option value="kraken">🐙 Kraken</option>
                              <option value="binance">🟡 Binance</option>
                              <option value="coinbase">🔵 Coinbase</option>
                              <option value="alpaca">🦙 Alpaca</option>
                            </>
                          ) : (
                            <option value="alpaca">🦙 Alpaca</option>
                          )}
                        </select>
                        <p className="text-xs text-slate-500 mt-1">
                          Ensure matching credentials are configured in Exchange Settings.
                        </p>
                      </div>

                      {/* Cash Limit */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          Cash Limit per Trade Cycle
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                          <input
                            type="number"
                            value={tradingForm.cash_limit}
                            onChange={e => setTradingForm(f => ({ ...f, cash_limit: e.target.value }))}
                            placeholder={`Max from agent capital ($${(form.capital || 0).toLocaleString()})`}
                            className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 pl-7 focus:border-blue-500 outline-none"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Leave blank to use capital × position_size_pct from the agent signal.
                        </p>
                      </div>

                      {/* Summary */}
                      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 space-y-2 text-xs text-slate-400">
                        <p className="text-white font-medium text-sm mb-2">Trading Configuration Summary</p>
                        <p>Mode: <span className="text-white">{tradingForm.trading_mode === 'paper' ? '📄 Paper (simulated locally)' : '⚡ Live (real orders)'}</span></p>
                        <p>Exchange: <span className="text-white capitalize">{tradingForm.exchange_id}</span></p>
                        <p>Asset Class: <span className="text-white capitalize">{tradingForm.exchange_type}</span></p>
                        <p>Max Cash: <span className="text-white">{tradingForm.cash_limit ? `$${Number(tradingForm.cash_limit).toLocaleString()}` : 'Dynamic (from signal position_size_pct)'}</span></p>
                      </div>
                    </>
                  )}
                </div>
              ) : modalTab === 'schedule' ? (
                /* ---------------------------------------------------------------- */
                /* Schedule Tab                                                      */
                /* ---------------------------------------------------------------- */
                <div className="space-y-5">
                  {/* Enable toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-900/60 rounded-lg border border-slate-700">
                    <div>
                      <p className="text-sm font-medium text-white">Enable Schedule</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {form.agent_type === 'signal'
                          ? 'Agent runs continuously within the time window. The LLM controls its own pacing via next_check_in_seconds.'
                          : 'Agent will run once daily at the configured time'}
                      </p>
                    </div>
                    <button
                      onClick={() => setScheduleForm(f => ({ ...f, schedule_enabled: !f.schedule_enabled }))}
                      className={`relative w-11 h-6 rounded-full transition ${scheduleForm.schedule_enabled ? 'bg-amber-500' : 'bg-slate-600'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${scheduleForm.schedule_enabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>

                  {scheduleForm.schedule_enabled && (
                    <>
                      {/* Days of week */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Active Days</label>
                        <div className="flex gap-2 flex-wrap">
                          {(['mon','tue','wed','thu','fri','sat','sun'] as const).map(day => (
                            <button
                              key={day}
                              onClick={() => setScheduleForm(f => ({
                                ...f,
                                schedule_days: f.schedule_days.includes(day)
                                  ? f.schedule_days.filter(d => d !== day)
                                  : [...f.schedule_days, day],
                              }))}
                              className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase transition border ${
                                scheduleForm.schedule_days.includes(day)
                                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                                  : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Timezone */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Timezone</label>
                        <select
                          value={scheduleForm.schedule_timezone}
                          onChange={e => setScheduleForm(f => ({ ...f, schedule_timezone: e.target.value }))}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                        >
                          <option value="America/New_York">Eastern (ET) — America/New_York</option>
                          <option value="America/Chicago">Central (CT) — America/Chicago</option>
                          <option value="America/Denver">Mountain (MT) — America/Denver</option>
                          <option value="America/Los_Angeles">Pacific (PT) — America/Los_Angeles</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </div>

                      {form.agent_type === 'signal' ? (
                        /* Signal agent: continuous window */
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-300 mb-1">Window Start</label>
                              <input
                                type="time"
                                value={scheduleForm.schedule_start_time}
                                onChange={e => setScheduleForm(f => ({ ...f, schedule_start_time: e.target.value }))}
                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-300 mb-1">Window End</label>
                              <input
                                type="time"
                                value={scheduleForm.schedule_end_time}
                                onChange={e => setScheduleForm(f => ({ ...f, schedule_end_time: e.target.value }))}
                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                              />
                            </div>
                          </div>
                          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-xs text-green-300">
                            Agent runs <strong>continuously</strong> from{' '}
                            <strong>{scheduleForm.schedule_start_time}</strong> to{' '}
                            <strong>{scheduleForm.schedule_end_time}</strong> ({scheduleForm.schedule_timezone}) on{' '}
                            <strong>{scheduleForm.schedule_days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}</strong>.
                            The LLM controls its own analysis cadence via <code>next_check_in_seconds</code> in its output. Trades fire when the LLM signals buy/sell.
                          </div>
                        </>
                      ) : (
                        /* Analyst agent: single daily run time */
                        <>
                          <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">Daily Run Time</label>
                            <input
                              type="time"
                              value={scheduleForm.schedule_start_time}
                              onChange={e => setScheduleForm(f => ({ ...f, schedule_start_time: e.target.value }))}
                              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                            />
                          </div>
                          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-300">
                            Agent will run once daily at <strong>{scheduleForm.schedule_start_time}</strong> ({scheduleForm.schedule_timezone}) on{' '}
                            <strong>{scheduleForm.schedule_days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}</strong>.
                            Use a pipeline with a <strong>Trigger Signal Window</strong> action to start signal trading based on analyst output.
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {!scheduleForm.schedule_enabled && (
                    <div className="text-center py-8 text-slate-500 text-sm">
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      Schedule is off — enable it above to configure automated runs
                    </div>
                  )}
                </div>
              ) : (
              /* ---------------------------------------------------------------- */
              /* Config Tab (existing content)                                     */
              /* ---------------------------------------------------------------- */
              <>
              {/* Presets */}
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Quick Presets</label>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map(p => (
                    <button
                      key={p.label}
                      onClick={() => applyPreset(p)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                        p.type === 'signal'
                          ? 'border-green-500/40 text-green-300 hover:bg-green-500/10'
                          : 'border-blue-500/40 text-blue-300 hover:bg-blue-500/10'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Agent Name <span className="text-red-400">*</span></label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. BTC Signal Agent"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Type row */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Agent Type</label>
                <div className="flex rounded-lg overflow-hidden border border-slate-600">
                  {(['signal', 'analyst'] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => setForm(f => ({ ...f, agent_type: t }))}
                      className={`flex-1 py-2 text-sm font-medium transition ${
                        form.agent_type === t
                          ? t === 'signal' ? 'bg-green-500/20 text-green-300' : 'bg-blue-500/20 text-blue-300'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      {t === 'signal' ? 'Signal' : 'Analyst'}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {form.agent_type === 'signal' ? 'Generates buy/sell/hold signals from market data' : 'Analyses trade history and portfolio risk'}
                </p>
              </div>

              {/* LLM Provider + Model row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">LLM Provider</label>
                  <div className="flex rounded-lg overflow-hidden border border-slate-600">
                    {([
                      { id: 'ollama' as const, label: '⚙ Ollama' },
                      { id: 'bedrock' as const, label: '☁ Bedrock' },
                    ]).map(p => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setForm(f => ({ ...f, llm_provider: p.id }));
                          loadModelsForProvider(p.id);
                        }}
                        className={`flex-1 py-2 text-sm font-medium transition ${
                          form.llm_provider === p.id
                            ? p.id === 'bedrock' ? 'bg-orange-500/20 text-orange-300' : 'bg-slate-500/20 text-slate-200'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {form.llm_provider === 'bedrock' && (
                    <p className="text-xs text-orange-300/70 mt-1">Configure credentials in LLM Provider settings</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Model</label>
                  <select
                    value={form.model}
                    onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    {availableModels.map(m => (
                      <option key={m.id} value={m.id}>{m.name || m.id}</option>
                    ))}
                  </select>
                  {form.llm_provider === 'bedrock' && (
                    <input
                      value={form.model}
                      onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                      placeholder="or type a custom Bedrock model ID..."
                      className="w-full mt-1.5 bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-xs font-mono focus:outline-none focus:border-orange-500"
                    />
                  )}
                </div>
              </div>

              {/* Strategy pairing */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Paired Strategy <span className="text-slate-500">(optional)</span></label>
                <select
                  value={form.strategy_id ?? ''}
                  onChange={e => setForm(f => ({ ...f, strategy_id: e.target.value || null }))}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="">— No strategy pairing —</option>
                  {strategies.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.symbol})</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">The agent will be aware of this strategy's parameters during execution.</p>
              </div>

              {/* Capital (create only) */}
              {!editingAgent && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Initial Capital ($)</label>
                  <input
                    type="number"
                    value={form.capital}
                    onChange={e => setForm(f => ({ ...f, capital: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              )}

              {/* Prompt — Basic / Advanced toggle */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-300">
                    System Prompt
                    {promptMode === 'basic' && (
                      <span className="text-slate-500 font-normal ml-2">{(form.prompt ?? '').length} chars (definition)</span>
                    )}
                    {promptMode === 'advanced' && (
                      <span className="text-slate-500 font-normal ml-2">{systemPromptText.length} chars (full)</span>
                    )}
                  </label>
                  <div className="flex items-center gap-1 bg-slate-900 border border-slate-600 rounded-lg p-0.5">
                    <button
                      onClick={() => setPromptMode('basic')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                        promptMode === 'basic' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      Basic
                    </button>
                    <button
                      onClick={switchToAdvanced}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                        promptMode === 'advanced' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      Advanced
                    </button>
                  </div>
                </div>

                {promptMode === 'basic' ? (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">
                      Define the agent's role and logic. This is injected into a structured system prompt template.
                    </p>
                    <textarea
                      value={form.prompt ?? ''}
                      onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))}
                      rows={6}
                      placeholder="Describe how this agent should behave, what indicators to prioritize, and under what conditions to act..."
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-500">
                        Full system prompt sent directly to the LLM — overrides the built-in template entirely.
                      </p>
                      {editingAgent && (
                        <button
                          onClick={resetToTemplate}
                          disabled={previewLoading}
                          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition"
                          title="Reset to assembled template"
                        >
                          {previewLoading
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <RotateCcw className="w-3 h-3" />}
                          Reset to template
                        </button>
                      )}
                    </div>
                    {previewLoading ? (
                      <div className="flex items-center justify-center py-6 text-slate-400 text-sm gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading assembled prompt...
                      </div>
                    ) : (
                      <textarea
                        value={systemPromptText}
                        onChange={e => setSystemPromptText(e.target.value)}
                        rows={12}
                        placeholder="Enter the complete system prompt..."
                        className="w-full bg-slate-900 border border-violet-500/40 rounded-lg px-3 py-2 text-white text-xs font-mono focus:outline-none focus:border-violet-500 resize-none leading-relaxed"
                      />
                    )}
                    <p className="text-xs text-violet-400/70 mt-1 flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      This prompt replaces the entire system message. You must include JSON output instructions.
                    </p>
                  </div>
                )}
              </div>
              </>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm">Cancel</button>
              <button
                onClick={saveAgent}
                disabled={saving || !form.name.trim() || (promptMode === 'basic' && !(form.prompt ?? '').trim()) || (promptMode === 'advanced' && !systemPromptText.trim())}
                className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingAgent ? 'Save Changes' : 'Create Agent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Input Modal (Signal agents need market data)                         */}
      {/* ------------------------------------------------------------------ */}
      {inputModal.open && inputModal.agent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">Execute: {inputModal.agent.name}</h2>
              <button onClick={() => setInputModal({ open: false, agent: null })} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-300">
                If this agent has data connections attached, they will auto-hydrate the input when the field below is left empty.
                Provide data manually to override.
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  {inputModal.agent?.agent_type === 'signal' ? 'Market Data' : 'Portfolio / Trade Data'}
                  <span className="text-slate-500 font-normal ml-2">(optional JSON — auto-fetched from connections)</span>
                </label>
                <textarea
                  value={inputData}
                  onChange={e => setInputData(e.target.value)}
                  rows={8}
                  placeholder={
                    inputModal.agent?.agent_type === 'signal'
                      ? '{"symbol": "BTC/USDT", "price": 65000, "rsi": 28.4, "macd": 120.5, "signal": 95.2, "volume": 12500}'
                      : '{"total_trades": 42, "win_rate": 0.61, "accumulated_pnl": 1250.50, "trades": [...]}'
                  }
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Context <span className="text-slate-500 font-normal">(optional JSON — symbol, timeframe, exchange)</span>
                </label>
                <input
                  value={inputContext}
                  onChange={e => setInputContext(e.target.value)}
                  placeholder='{"symbol": "BTC/USDT", "timeframe": "1h", "exchange": "binance"}'
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
              <button onClick={() => setInputModal({ open: false, agent: null })} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm">Cancel</button>
              <button
                onClick={() => {
                  let ctx: Record<string, unknown> = {};
                  try { ctx = JSON.parse(inputContext || '{}'); } catch { ctx = {}; }
                  runAgent(inputModal.agent!, inputData, ctx);
                }}
                className="px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-medium flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                Run Agent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Execution Modal                                                       */}
      {/* ------------------------------------------------------------------ */}
      {execModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">{execModal.agentName}</h2>
              {execModal.status !== 'running' && (
                <button onClick={() => setExecModal(prev => ({ ...prev, open: false }))} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              )}
            </div>
            <div className="p-6 space-y-4">
              {execModal.status === 'running' && (
                <div className="flex items-center gap-3 text-slate-300">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                  <span>Running agent...</span>
                  <span className="text-slate-500 ml-auto font-mono text-sm">{(execModal.elapsed / 1000).toFixed(1)}s</span>
                </div>
              )}
              {execModal.status === 'completed' && execModal.result && (
                <>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-300 font-medium">Completed</span>
                    {execModal.result.duration_ms && (
                      <span className="text-slate-500 ml-auto font-mono text-sm">{(execModal.result.duration_ms / 1000).toFixed(2)}s</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span>Model: <span className="text-slate-300">{execModal.result.model}</span></span>
                    <span>Parse attempts: <span className="text-slate-300">{execModal.result.parse_attempts}</span></span>
                  </div>
                  {renderResult(execModal.result)}
                </>
              )}
              {execModal.status === 'failed' && execModal.result && (
                <>
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-300 font-medium">Failed</span>
                  </div>
                  <AgentErrorBanner error={execModal.result.error} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* History Modal                                                         */}
      {/* ------------------------------------------------------------------ */}
      {historyModal.open && historyModal.agent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">Execution History: {historyModal.agent.name}</h2>
              <button onClick={() => setHistoryModal({ open: false, agent: null, executions: [] })} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-2">
              {historyModal.executions.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No executions recorded yet.</p>
              ) : historyModal.executions.map(exec => (
                <div key={exec.id} className="bg-slate-900/60 rounded-lg border border-slate-700/50 overflow-hidden">
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-700/30"
                    onClick={() => toggleExecExpand(exec.id)}
                  >
                    {exec.success
                      ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      : <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    }
                    <span className="text-xs text-slate-400">{formatDate(exec.started_at)}</span>
                    {exec.duration_ms && (
                      <span className="text-xs text-slate-500">{(exec.duration_ms / 1000).toFixed(2)}s</span>
                    )}
                    <span className="text-xs text-slate-500 ml-auto">{exec.model}</span>
                    {expandedExec.has(exec.id) ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                  </div>
                  {expandedExec.has(exec.id) && (
                    <div className="px-3 pb-3">
                      {exec.error && (
                        <div className="mb-2"><AgentErrorBanner error={exec.error} compact /></div>
                      )}
                      {renderResult(exec)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Connection Data Preview Modal                                        */}
      {/* ------------------------------------------------------------------ */}
      {dataPreviewModal.open && dataPreviewModal.agent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-sky-400" />
                <h2 className="text-xl font-bold text-white">Connection Data: {dataPreviewModal.agent.name}</h2>
              </div>
              <button onClick={() => setDataPreviewModal({ open: false, agent: null, data: null, loading: false })} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {dataPreviewModal.loading ? (
                <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Fetching connection data...
                </div>
              ) : dataPreviewModal.data === null ? (
                <p className="text-slate-500 text-center py-8 text-sm">No data returned.</p>
              ) : (
                <pre className="text-xs text-sky-200 font-mono bg-slate-950 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap border border-slate-700/50">
                  {JSON.stringify(dataPreviewModal.data, null, 2)}
                </pre>
              )}
            </div>
            <div className="px-6 py-3 border-t border-slate-700 text-xs text-slate-500">
              This is the raw data fetched from all IN connections — exactly what gets injected as input to the LLM.
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Connections side panel                                               */}
      {/* ------------------------------------------------------------------ */}
      {connPanel.open && connPanel.agent && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setConnPanel({ open: false, agent: null })}
          />
          {/* Slide-in panel */}
          <div className="fixed right-0 top-0 bottom-0 w-96 bg-slate-800 border-l border-slate-700 z-50 flex flex-col shadow-2xl">
            {/* Panel header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-700">
              <div>
                <h2 className="font-bold text-white">Data Connections</h2>
                <p className="text-xs text-slate-400 mt-0.5">{connPanel.agent.name}</p>
              </div>
              <button
                onClick={() => setConnPanel({ open: false, agent: null })}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Panel body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {connsLoading ? (
                <div className="flex items-center justify-center py-8 text-slate-400 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </div>
              ) : agentConns.length === 0 ? (
                <div className="text-center py-8">
                  <Link className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No connections attached.</p>
                  <p className="text-slate-600 text-xs mt-1">
                    Attach a data source so this agent receives real market or portfolio data when executed.
                  </p>
                </div>
              ) : (
                agentConns.map((ac) => (
                  <div key={ac.id} className="bg-slate-900/60 border border-slate-700 rounded-xl p-3.5 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white text-sm truncate">{ac.connection.name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${DIR_COLOR[ac.direction]}`}>
                          {ac.direction.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                        <span>{ac.connection.connection_type.replace('_', ' ')}</span>
                        {ac.connection.provider && <span>· {ac.connection.provider}</span>}
                      </div>
                      {ac.config && (
                        <details className="mt-1.5">
                          <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">Config</summary>
                          <pre className="text-xs text-slate-400 font-mono mt-1 bg-slate-900 rounded p-1.5 overflow-x-auto max-h-20">
                            {JSON.stringify(ac.config, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => {
                          setAttachForm({
                            connection_id: ac.connection_id,
                            direction: ac.direction as Direction,
                            config: ac.config ? JSON.stringify(ac.config, null, 2) : '',
                          });
                          setEditingConnId(ac.id);
                          setAttachConfigError(null);
                          setAttachModal(true);
                        }}
                        className="p-1.5 rounded hover:bg-blue-500/20 text-slate-500 hover:text-blue-400 transition"
                        title="Edit config"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDetach(ac.id)}
                        className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition"
                        title="Detach connection"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Attach button */}
            <div className="p-4 border-t border-slate-700">
              <button
                onClick={() => {
                  setAttachForm({ connection_id: allConns[0]?.id || '', direction: 'in', config: '' });
                  setAttachConfigError(null);
                  setEditingConnId(null);
                  setAttachModal(true);
                }}
                disabled={allConns.length === 0}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium disabled:opacity-50 transition"
              >
                <Plus className="w-4 h-4" />
                {allConns.length === 0 ? 'No connections available' : 'Attach Connection'}
              </button>
              {allConns.length === 0 && (
                <p className="text-xs text-slate-500 text-center mt-1.5">
                  Create connections on the Connections page first.
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Attach connection mini-modal */}
      {attachModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-slate-700">
              <h3 className="font-bold text-white">{editingConnId ? 'Edit Connection Config' : 'Attach Connection'}</h3>
              <button onClick={() => { setAttachModal(false); setAttachConfigError(null); setEditingConnId(null); }} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Connection</label>
                <select
                  value={attachForm.connection_id}
                  onChange={e => setAttachForm(f => ({ ...f, connection_id: e.target.value }))}
                  disabled={!!editingConnId}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50"
                >
                  {allConns.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.connection_type}) — {c.direction}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Direction for this agent</label>
                <select
                  value={attachForm.direction}
                  onChange={e => setAttachForm(f => ({ ...f, direction: e.target.value as Direction }))}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="in">IN — feed data into agent</option>
                  <option value="out">OUT — push results out</option>
                  <option value="both">BOTH</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">
                  Config override <span className="text-slate-600">(optional JSON)</span>
                </label>
                <textarea
                  value={attachForm.config}
                  onChange={e => { setAttachForm(f => ({ ...f, config: e.target.value })); setAttachConfigError(null); }}
                  rows={3}
                  placeholder='{"symbols": ["BTC/USDT"], "timeframe": "1h", "limit": 50}'
                  className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-xs font-mono focus:outline-none resize-none ${attachConfigError ? 'border-red-500 focus:border-red-500' : 'border-slate-600 focus:border-blue-500'}`}
                />
                {attachConfigError
                  ? <p className="text-xs text-red-400 mt-1">{attachConfigError}</p>
                  : <p className="text-xs text-slate-600 mt-1">Overrides the connection's default config for this agent.</p>
                }
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-700">
              <button onClick={() => { setAttachModal(false); setAttachConfigError(null); }} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm">
                Cancel
              </button>
              <button
                onClick={handleAttach}
                disabled={attaching || !attachForm.connection_id}
                className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {attaching && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingConnId ? 'Save Config' : 'Attach'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Agent?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This will also remove all execution history.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Live Agent Stream Modal                                              */}
      {/* ------------------------------------------------------------------ */}
      {streamModal.open && streamModal.agent && (
        <AgentStreamModal
          agentId={streamModal.agent.id}
          agentName={streamModal.agent.name}
          agentStatus={streamModal.agent.status}
          onClose={() => setStreamModal({ open: false, agent: null })}
        />
      )}
    </div>
  );
};

export default AgentsPage;
